import { useCallback, useState } from 'react';
import useFetch, { ServiceParams, ServiceResponse } from '../hooks/useFetch';
import EndPoints from '../config/EndPoint';
import { CostCenter, CostCenterPayload } from '../models/CostCenterModel';

export type Events = Partial<{
  onSuccess: (r: ServiceResponse) => void;
  onError: (r: ServiceResponse) => void;
}>;

const qs = (filters?: Record<string, any>) => {
  if (!filters) return '';
  return Object.entries(filters)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
};

const useCostCenterService = () => {
  const { doGet, doPost, doPut, doDelete } = useFetch();
  const [fetching, setFetching] = useState<Record<string, boolean>>({});

  const setFetch = useCallback((key: string, val: boolean) => {
    setFetching(prev => ({ ...prev, [key]: val }));
  }, []);

  const list = useCallback(
    (filters?: Record<string, any>, events: Events = {}, signal?: AbortSignal) =>
      doGet({
        url: `${EndPoints.COST_CENTER.LIST}${qs(filters) ? `?${qs(filters)}` : ''}`,
        setFetching: v => setFetch('list', v),
        signal,
        ...events,
      } as ServiceParams),
    [doGet, setFetch]
  );

  const store = useCallback(
    (payload: CostCenterPayload, events: Events = {}) =>
      doPost({
        url: EndPoints.COST_CENTER.STORE,
        body: payload,
        setFetching: v => setFetch('store', v),
        ...events,
      } as ServiceParams),
    [doPost, setFetch]
  );

  const update = useCallback(
    (id: number, payload: CostCenterPayload, events: Events = {}) =>
      doPut({
        url: EndPoints.COST_CENTER.UPDATE.replace(':id', String(id)),
        body: payload,
        setFetching: v => setFetch('update', v),
        ...events,
      } as ServiceParams),
    [doPut, setFetch]
  );

  const remove = useCallback(
    (id: number, events: Events = {}) =>
      doDelete({
        url: EndPoints.COST_CENTER.DELETE.replace(':id', String(id)),
        setFetching: v => setFetch('delete', v),
        ...events,
      } as ServiceParams),
    [doDelete, setFetch]
  );


  const create = useCallback(
    (events: Events = {}) =>
      doGet({
        url: EndPoints.COST_CENTER.CREATE,
        setFetching: v => setFetch('create', v),
        ...events,
      } as ServiceParams),
    [doGet, setFetch]
  );

  
  const edit = useCallback(
    (id: number, events: Events = {}) =>
      doGet({
        url: EndPoints.COST_CENTER.EDIT.replace(':id', String(id)),
        setFetching: (v) => setFetch('edit', v),
        ...events,
      } as ServiceParams),
    [doGet, setFetch]
  );

  const byCompany = useCallback(
      (companyId: number, events?: Events, signal?: AbortSignal) =>
        doGet({ 
          url: EndPoints.COST_CENTER.BY_COMPANY.replace(':id', String(companyId)), 
          setFetching: v => setFetch('byCompany', v), 
          signal,
          ...events 
        } as ServiceParams),
      [doGet, setFetch]
    );
  


  return { fetching, list, store, update, remove, create , edit, byCompany };
};

export default useCostCenterService;
